import { axiosInstance } from '../axios.config';
import { ListingPhoto } from '../types/photo.types';

/** Master §7: who is selling, shown on listings */
export type SellerTypeLabel = 'Seller direct' | 'Corporate seller' | 'Wholesaler' | 'Agent';

/** FR-39: how an agent's listing is released */
export type ReleaseMode = 'all' | 'network_first';

/** PRD 3.3 / 3.6 listing fields shared by the listing responses */
export interface ListingLifecycleFields {
  sellerTypeLabel?: SellerTypeLabel | null;
  /** Set while an accepted offer freezes the listing clock */
  clockFrozenAt?: string | null;
  relistCount?: number;
  releaseMode?: ReleaseMode;
  /** Until this time only the agent's network sees the listing */
  networkHeadStartEndsAt?: string | null;
}

/** Admin listing settings (Master §5.3) */
export interface ListingSettings {
  minRelistReductionPercent: number;
  relistWindowDays: number;
}

/** PRD 3.3: what relisting an expired listing takes */
export interface RelistQuote {
  listingId: string;
  canRelist: boolean;
  reason: string | null;
  lastPrice: number;
  maxPrice: number;
  minReductionPercent: number;
  offersCount: number;
  bestOffer: number | null;
}

// Types based on backend DTOs
export interface CreateListingDto {
  title: string;
  description: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  apn?: string;
  propertyType: string;
  sqft: number;
  bedsCount: number;
  bathsFull: number;
  bathsHalf: number;
  lotSize?: number;
  yearBuilt?: number;
  askingPrice: number;
  rehabDetails?: string;
  rehabType?: string;
  rehabEstimate?: number;
  /** FR-08: the accepted seller invite this listing is posted under (fee + first look bind to it) */
  referralInviteId?: string;
  unpermittedAdditionsDescription?: string;
  allowTier1Access: boolean;
  bidDeadline?: string;
  notifyNetwork?: boolean; // Story 6.7: Notify network when listing is published
  taggedUserIds?: string[]; // Story 6.6: Tag specific buyers in listing
  // Additional deal details
  sellerType?: string;
  /** FR-39: agents only – release to all buyers, or to their own network first */
  releaseMode?: ReleaseMode;
  saleType?: string;
  financingType?: string;
  showingInstruction?: string;
  physicalInspection?: string;
  occupancy?: string;
  marketStatus?: string;
}

// Seller information
export interface SellerDto {
  id: string;
  flipiqAuthUserId: string; // FlipIQ Auth service user ID for comparison with frontend auth state
  name: string;
  organizationName: string;
  photoUrl?: string; // Seller's profile photo URL
  licenseInfo?: string; // Only includes license if seller has one in the listing's state
  tier: number; // User's tier (1-4) for credibility display
  totalDeals: number; // Total completed deals for reputation
  memberSince?: string | null; // When the seller joined (ISO date)
}

// Original listing response (for list views, create, update operations)
export interface ListingResponseDto extends ListingLifecycleFields {
  id: string;
  userId: string;
  orgId: string;
  title: string;
  description: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  apn: string | null;
  propertyType: string;
  sqft: number;
  bedsCount: number;
  bathsFull: number;
  bathsHalf: number;
  bathsCount: number;
  lotSize: number | null;
  yearBuilt: number | null;
  askingPrice: number;
  rehabDetails: string | null;
  unpermittedAdditionsDescription: string | null;
  allowTier1Access: boolean;
  latitude: number | null;
  longitude: number | null;
  bidDeadline: Date | null;
  isDeadlinePassed: boolean;
  expiresAt: Date | string | null;
  daysUntilExpiration: number | null;
  status: string;
  /** MKT-43: set while the listing is held for (or was offered on by) the inviting company */
  firstLookCompanyName?: string | null;
  /** The operator invited the seller, so firstLookCompanyName is its name */
  firstLookNamed?: boolean;
  firstLookEndsAt?: string | null;
  firstLookState?: string | null;
  createdAt: Date;
  updatedAt: Date;
  photos: ListingPhoto[];
  viewCount: number;
  bidCount: number;
}

// Analytics DTOs for listing performance metrics
export interface ViewAnalyticsDto {
  totalViews: number;
  uniqueUsers: number;
}

export interface BuyBoxMatchAnalyticsDto {
  matchingBuyBoxes: number;
  matchingOrganizations: number;
}

export interface DailyViewsDto {
  date: string;
  views: number;
}

export interface ListingAnalyticsDto {
  views: ViewAnalyticsDto;
  buyBoxMatches: BuyBoxMatchAnalyticsDto;
  bidCount: number;
  viewsPerDay?: DailyViewsDto[];
  /** Highest live bid on the listing (owner only); may arrive as a numeric string */
  highestBidAmount?: number | string | null;
}

// Listing detail response with tier-based access control
export interface ListingDetailResponseDto extends ListingLifecycleFields {
  // Always included fields
  id: string;
  /** Live (active + countered) bid count; Postgres counts may arrive as strings */
  bidCount?: number | string | null;
  title: string;
  description: string;
  city: string;
  state: string;
  zip?: string; // Optional: hidden for SOLD/CLOSED listings (MKT-38)
  propertyType: string;
  sqft: number;
  bedsCount: number;
  bathsFull: number;
  bathsHalf: number;
  bathsCount: number;
  askingPrice: number;
  photos: ListingPhoto[];
  status: string;
  createdAt: Date;
  updatedAt: Date;

  // Access-level restricted fields (only if hasFullAccess = true)
  street?: string;
  apn?: string | null;
  county?: string | null;
  lotSize?: number | null;
  yearBuilt?: number | null;
  rehabDetails?: string | null;
  rehabType?: string | null;
  rehabEstimate?: number | null;
  unpermittedAdditionsDescription?: string | null;
  latitude?: number | null;
  longitude?: number | null;

  // Deal details
  sellerType?: string | null;
  saleType?: string | null;
  financingType?: string | null;
  showingInstruction?: string | null;
  physicalInspection?: string | null;
  occupancy?: string | null;
  marketStatus?: string | null;

  // Bid deadline information
  bidDeadline?: Date | null;
  isDeadlinePassed: boolean;

  // Expiration information
  expiresAt?: Date | string | null;
  daysUntilExpiration?: number | null;

  // MKT-43 first look (owner and admins only)
  firstLookCompanyName?: string | null;
  /** The operator invited the seller, so firstLookCompanyName is its name */
  firstLookNamed?: boolean;
  firstLookEndsAt?: string | null;
  firstLookState?: string | null;

  // Access control metadata
  hasFullAccess: boolean;
  requiresVerification: boolean;
  canBid: boolean; // true if user can submit a bid (email verified + tier access)
  canEdit: boolean; // true if user is owner or admin
  isDismissed: boolean; // true if user has dismissed/passed on this listing

  // Seller information
  seller: SellerDto;

  // Story 6.6: Tag information
  isTaggedForYou?: boolean;
  taggedBy?: { name: string; id: string };

  // Story 6.7: Network notification info (owner only)
  networkNotifiedAt?: string | null;
}

/**
 * Listing card data returned from browse endpoint (limited public data)
 */
export interface ListingCardData {
  id: string;
  primaryPhotoUrl: string | null;
  price: number;
  city: string;
  state: string;
  propertyType: string;
  bedsCount: number;
  bathsCount: number;
  sqft: number;
  createdAt: string;
}

class ListingsService {
  /**
   * Create a new listing
   */
  async createListing(data: CreateListingDto): Promise<ListingResponseDto> {
    const response = await axiosInstance.post<ListingResponseDto>('/listings', data);
    return response.data;
  }

  /**
   * Get all listings with optional filters
   * The backend returns a paginated response with ListingCardDto data
   */
  async getAllListings(filters?: {
    status?: string;
    city?: string;
    state?: string;
    propertyType?: string;
    minPrice?: number;
    maxPrice?: number;
    userId?: string;
    page?: number;
    limit?: number;
  }): Promise<ListingResponseDto[]> {
    const response = await axiosInstance.get<{ listings: ListingCardData[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>('/listings', {
      params: { page: 1, limit: 100, ...filters },
    });

    // Transform ListingCardData to match ListingResponseDto structure
    return response.data.listings.map(card => ({
      id: card.id,
      userId: '',
      orgId: '',
      title: '',
      description: '',
      street: '', // Not available in card data
      city: card.city,
      state: card.state,
      zip: '',
      apn: null,
      propertyType: card.propertyType,
      sqft: card.sqft,
      bedsCount: card.bedsCount,
      bathsFull: 0,
      bathsHalf: 0,
      bathsCount: card.bathsCount,
      lotSize: null,
      yearBuilt: null,
      askingPrice: card.price,
      rehabDetails: null,
      unpermittedAdditionsDescription: null,
      allowTier1Access: false,
      latitude: null,
      longitude: null,
      bidDeadline: null,
      isDeadlinePassed: false,
      expiresAt: null,
      daysUntilExpiration: null,
      status: 'Open',
      createdAt: new Date(card.createdAt),
      updatedAt: new Date(card.createdAt),
      photos: card.primaryPhotoUrl ? [{ id: '', url: card.primaryPhotoUrl, order: 0, caption: null, fileSize: 0, mimeType: 'image/jpeg' as const, createdAt: new Date() }] : [],
      viewCount: 0,
      bidCount: 0,
    }));
  }

  /**
   * Get a single listing by ID (with tier-based access control)
   */
  async getListingById(id: string): Promise<ListingDetailResponseDto> {
    const response = await axiosInstance.get<ListingDetailResponseDto>(`/listings/${id}`);
    return response.data;
  }

  /**
   * Track a listing view
   */
  async trackListingView(id: string, sessionId: string): Promise<void> {
    try {
      await axiosInstance.post(`/listings/${id}/views`, { sessionId });
    } catch (error) {
      // Silently fail - view tracking should not block user experience
      console.warn('Failed to track listing view:', error);
    }
  }

  /**
   * Update a listing
   */
  async updateListing(
    id: string,
    data: Partial<CreateListingDto>,
  ): Promise<ListingResponseDto> {
    const response = await axiosInstance.put<ListingResponseDto>(
      `/listings/${id}`,
      data,
    );
    return response.data;
  }

  /**
   * Mark a listing as sold
   * @param listingId - The listing ID
   * @param winningBuyerId - Optional buyer ID (if sold on marketplace)
   */
  async markAsSold(
    listingId: string,
    winningBidId?: string,
  ): Promise<{ message: string; listing: ListingResponseDto }> {
    // PRD 6.6: the seller picks a bid; the server resolves the (masked) buyer
    const response = await axiosInstance.put<{ message: string; listing: ListingResponseDto }>(
      `/listings/${listingId}/mark-sold`,
      { winningBidId },
    );
    return response.data;
  }

  /**
   * Withdraw a listing from the marketplace
   * @param listingId - The listing ID
   * @param reason - Optional reason for withdrawing
   */
  async withdrawListing(
    listingId: string,
    reason?: string,
  ): Promise<{ message: string; listing: ListingResponseDto }> {
    const response = await axiosInstance.put<{ message: string; listing: ListingResponseDto }>(
      `/listings/${listingId}/withdraw`,
      { reason },
    );
    return response.data;
  }

  /** Admin: minimum relist price cut and relist window */
  async getListingSettings(): Promise<ListingSettings> {
    const response = await axiosInstance.get<ListingSettings>('/admin/listing-settings');
    return response.data;
  }

  async updateListingSettings(payload: Partial<ListingSettings>): Promise<ListingSettings> {
    const response = await axiosInstance.put<ListingSettings>('/admin/listing-settings', payload);
    return response.data;
  }

  /** PRD 3.3: the last price, the highest relist price allowed, and the offers report */
  async getRelistQuote(listingId: string): Promise<RelistQuote> {
    const response = await axiosInstance.get<RelistQuote>(`/listings/${listingId}/relist`);
    return response.data;
  }

  /** PRD 3.3: relist an expired listing at a lower price; it goes back through first look */
  async relistListing(listingId: string, askingPrice: number): Promise<ListingResponseDto> {
    const response = await axiosInstance.post<ListingResponseDto>(`/listings/${listingId}/relist`, {
      askingPrice,
    });
    return response.data;
  }

  /**
   * Delete a listing
   */
  async deleteListing(id: string): Promise<void> {
    await axiosInstance.delete(`/listings/${id}`);
  }

  /**
   * Get my listings
   */
  async getMyListings(): Promise<ListingResponseDto[]> {
    const response = await axiosInstance.get<ListingResponseDto[]>(
      '/listings/my/all',
    );
    return response.data;
  }

  /**
   * Seed mock listings (development only)
   */
  async seedMockListings(): Promise<{
    message: string;
    count: number;
    listings: ListingResponseDto[];
  }> {
    const response = await axiosInstance.post<{
      message: string;
      count: number;
      listings: ListingResponseDto[];
    }>('/listings/seed-mock-data');
    return response.data;
  }

  /**
   * Get presigned URL for photo upload
   */
  async getPhotoUploadUrl(
    listingId: string,
    data: { fileName: string; fileSize: number; mimeType: string }
  ): Promise<{ uploadUrl: string; photoId: string; key: string }> {
    const response = await axiosInstance.post(
      `/listings/${listingId}/photos/upload-url`,
      data
    );
    return response.data;
  }

  /**
   * Confirm photo upload after S3 upload
   */
  async confirmPhotoUpload(
    listingId: string,
    data: { s3Key: string; mimeType: string; fileSize: number; caption?: string }
  ): Promise<void> {
    await axiosInstance.post(`/listings/${listingId}/photos/confirm`, data);
  }

  /**
   * Delete a photo
   */
  async deletePhoto(listingId: string, photoId: string): Promise<void> {
    await axiosInstance.delete(`/listings/${listingId}/photos/${photoId}`);
  }

  /**
   * Reorder photos via drag-and-drop
   * Array index determines new order (0 = primary photo)
   */
  async reorderPhotos(listingId: string, photoIds: string[]): Promise<void> {
    await axiosInstance.put(`/listings/${listingId}/photos/reorder`, { photoIds });
  }

  /**
   * Get all photos for a listing
   */
  async getListingPhotos(listingId: string): Promise<ListingPhoto[]> {
    const response = await axiosInstance.get(`/listings/${listingId}/photos`);
    return response.data;
  }

  /**
   * Tag users in a listing
   * Story 6.6: Tag Users in Listings (AC#11)
   */
  async tagBuyers(listingId: string, taggedUserIds: string[]): Promise<void> {
    await axiosInstance.post(`/listings/${listingId}/tags`, { taggedUserIds });
  }

  /**
   * Get tags for a listing (owner only)
   * Story 6.6: Returns list of tagged users for editing
   */
  async getListingTags(listingId: string): Promise<{
    id: string;
    listingId: string;
    taggedUser: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    createdAt: string;
  }[]> {
    const response = await axiosInstance.get(`/listings/${listingId}/tags`);
    return response.data;
  }

  /**
   * Get listings where the current user is tagged
   * Story 6.6: Tag Users in Listings (AC#9)
   */
  async getTaggedListings(): Promise<ListingCardData[]> {
    const response = await axiosInstance.get<ListingCardData[]>('/listings/tagged');
    return response.data;
  }

  /**
   * Notify network about a listing
   * Story 6.7: "Notify Network Now" feature (AC#6)
   */
  async notifyNetworkNow(listingId: string): Promise<void> {
    await axiosInstance.patch(`/listings/${listingId}/notify-network`);
  }

  /**
   * Get analytics for a listing (creator-only)
   * Returns view counts, buy box matches, and bid count
   */
  async getListingAnalytics(
    listingId: string,
    includeTimeSeries: boolean = false
  ): Promise<ListingAnalyticsDto> {
    const response = await axiosInstance.get<ListingAnalyticsDto>(
      `/listings/${listingId}/analytics`,
      { params: { includeTimeSeries: includeTimeSeries.toString() } }
    );
    return response.data;
  }

  /**
   * Get batch analytics for multiple listings (creator-only)
   * Returns view counts, buy box matches, and bid count for multiple listings
   * Optimized for "My Listings" page to reduce N+1 queries
   */
  async getBatchListingAnalytics(
    listingIds: string[]
  ): Promise<Record<string, ListingAnalyticsDto>> {
    const response = await axiosInstance.post<Record<string, ListingAnalyticsDto>>(
      '/listings/analytics/batch',
      { listingIds }
    );
    return response.data;
  }
}

// Export singleton instance
export const listingsService = new ListingsService();
export default listingsService;
